import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';

/**
 * Component that renders highlighted text with comment indicators
 */
const CommentHighlight = ({ 
  children, 
  blockIndex, 
  comments = [], 
  onCommentClick,
  className = '',
  style = {}
}) => {
  const [hoveredComment, setHoveredComment] = useState(null);

  // Get comments for this specific block
  const blockComments = comments.filter(comment => comment.blockIndex === blockIndex);

  /**
   * Render highlighted text with comment indicators
   * Use useMemo to prevent re-processing on every render
   */
  const highlightedContent = useMemo(() => {
    // If no comments, just return the original text
    if (blockComments.length === 0) {
      return children;
    }

    // Extract the text content from children (it should be a string)
    const textContent = typeof children === 'string' ? children : String(children);
    let lastIndex = 0;
    const elements = [];

    // Sort comments by start position, then by end position
    const sortedComments = [...blockComments].sort((a, b) => {
      if (a.startOffset !== b.startOffset) {
        return a.startOffset - b.startOffset;
      }
      return a.endOffset - b.endOffset;
    });

    // Group overlapping comments together
    const processedRanges = new Set();

    sortedComments.forEach((comment, commentIndex) => {
      const { startOffset, endOffset, highlightedText, id } = comment;
      
      // Skip if we've already processed this exact range
      const rangeKey = `${startOffset}-${endOffset}`;
      if (processedRanges.has(rangeKey)) {
        return;
      }
      processedRanges.add(rangeKey);
      
      // Skip if this range overlaps with already processed text
      if (startOffset < lastIndex) {
        return;
      }
      
      // Add text before the highlight
      if (startOffset > lastIndex) {
        const beforeText = textContent.slice(lastIndex, startOffset);
        if (beforeText) {
          elements.push(
            <span key={`text-${commentIndex}`}>
              {beforeText}
            </span>
          );
        }
      }

      // Find all comments that share this exact range
      const commentsInRange = sortedComments.filter(
        c => c.startOffset === startOffset && c.endOffset === endOffset
      );

      // Add the highlighted text with comment indicator
      const highlightText = textContent.slice(startOffset, endOffset);
      if (highlightText) {
        const isHovered = commentsInRange.some(c => hoveredComment === c.id);
        const firstComment = commentsInRange[0];
        
        elements.push(
          <span
            key={`highlight-${startOffset}-${endOffset}`}
            className={`comment-highlight ${isHovered ? 'hovered' : ''}`}
            style={{
              backgroundColor: isHovered 
                ? 'rgba(59, 130, 246, 0.4)' 
                : 'rgba(59, 130, 246, 0.25)',
              border: isHovered 
                ? '1px solid rgba(59, 130, 246, 0.6)' 
                : '1px solid rgba(59, 130, 246, 0.3)',
              cursor: 'pointer',
              position: 'relative',
              borderRadius: '3px',
              transition: 'all 0.2s ease',
              padding: '1px 2px',
              margin: '0 1px',
              ...style
            }}
            onClick={() => onCommentClick && onCommentClick(firstComment)}
            onMouseEnter={() => setHoveredComment(firstComment.id)}
            onMouseLeave={() => setHoveredComment(null)}
            title={commentsInRange.length > 1 ? `${commentsInRange.length} comments` : '1 comment'}
          >
            {highlightText}
          </span>
        );
      }

      lastIndex = Math.max(lastIndex, endOffset);
    });

    // Add remaining text
    if (lastIndex < textContent.length) {
      const remainingText = textContent.slice(lastIndex);
      if (remainingText) {
        elements.push(
          <span key="text-end">
            {remainingText}
          </span>
        );
      }
    }

    return elements.length > 0 ? elements : children;
  }, [blockComments, children, hoveredComment, onCommentClick, style]);

  return (
    <span
      className={`comment-highlight-container ${className}`}
      data-block-index={blockIndex}
    >
      {highlightedContent}
    </span>
  );
};

CommentHighlight.propTypes = {
  children: PropTypes.node.isRequired,
  blockIndex: PropTypes.number.isRequired,
  comments: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    blockIndex: PropTypes.number.isRequired,
    startOffset: PropTypes.number.isRequired,
    endOffset: PropTypes.number.isRequired,
    highlightedText: PropTypes.string.isRequired,
    content: PropTypes.string.isRequired,
    user: PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string,
      email: PropTypes.string,
      profile_image: PropTypes.string
    }).isRequired,
    createdAt: PropTypes.string.isRequired
  })),
  onCommentClick: PropTypes.func,
  className: PropTypes.string,
  style: PropTypes.object
};

export default CommentHighlight;
